import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Slider,
  Typography,
} from "@mui/material";
import { Message } from "./chat";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Pause,
  PlayArrow,
} from "@mui/icons-material";

interface ChatAudioMessageProps {
  msg: Message;
  avatarLisUrl: string;
  onDone?: () => void;
}

const ChatAudioMessage: React.FC<ChatAudioMessageProps> = ({
  msg,
  avatarLisUrl,
}) => {
  const [playing, setPlaying] = useState(false);
  const [showTranscription, setShowTranscription] = useState(true);
  const textTranscribed = msg.text || "Transcrição não disponível";
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const audioRef = React.useRef<HTMLAudioElement | null>(
    msg.audioUrl ? new Audio(msg.audioUrl) : null
  );
  const handleTranscription = () => {
    setShowTranscription(!showTranscription);
  };

  const playAudio = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      window?.dispatchEvent(
        new CustomEvent("audioStarted", { detail: msg.audioUrl })
      );
      audio.play();
      setPlaying(true);
    }
  };

  const handleSeekValue = (value: number | number[]) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = Array.isArray(value) ? value[0] : value;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  useEffect(() => {
    const audio = audioRef.current;

    if (audio) {
      const handleEnded = () => {
        setPlaying(false);
      };

      audio.addEventListener("ended", handleEnded);
      return () => {
        audio.removeEventListener("ended", handleEnded);
      };
    }
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const play = async () => {
      try {
        await audio.play();
        setPlaying(true);
      } catch (err) {
        console.warn("Autoplay bloqueado pelo navegador:", err);
      }
    };

    play();
  }, []);

  useEffect(() => {
    const handleStop = () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setPlaying(false);
      }
    };

    const handleAudioStarted = (e: Event) => {
      const customEvent = e as CustomEvent<string>;
      if (customEvent.detail !== msg.audioUrl) {
        handleStop();
      }
    };

    window?.addEventListener("stopAllAudios", handleStop);
    window?.addEventListener("audioStarted", handleAudioStarted);

    return () => {
      window?.removeEventListener("stopAllAudios", handleStop);
      window?.removeEventListener("audioStarted", handleAudioStarted);
    };
  }, [msg.audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  const formatTime = (seconds: number) => {
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60)
      .toString()
      .padStart(2, "0");
    return `${min}:${sec}`;
  };

  return (
    <Box
      sx={{
        maxWidth: { xs: "90%", sm: "94%" },
        width: "100%",
        ml: msg.from === "user" ? "auto" : 0,
        mr: msg.from === "user" ? 0 : "auto",
        px: 1,
        display: "flex",
        alignItems: "flex-end",
        flexDirection: msg.from === "user" ? "row-reverse" : "row",
        gap: 1,
      }}
    >
      <Avatar
        src={msg.from === "user" ? "/images/avatar-user.svg" : avatarLisUrl}
        alt={msg.from === "user" ? "Usuário" : "Lis"}
        sx={{
          width: 24,
          height: 24,
        }}
      />
      <Box
        sx={{
          bgcolor: msg.from === "user" ? "#D6D7E0" : "white",
          borderRadius: "12px",
          borderBottomLeftRadius: msg.from === "user" ? "12px" : 0,
          borderBottomRightRadius: msg.from === "user" ? 0 : "12px",
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1, sm: 1.5 },
          position: "relative",
          fontSize: { xs: 14, sm: 16 },
          overflowWrap: "break-word",
          wordBreak: "break-word",
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton color={"primary"} size="large" onClick={playAudio}>
              {playing ? <Pause /> : <PlayArrow />}
            </IconButton>
            <Slider
              min={0}
              max={duration}
              value={currentTime}
              step={0.1}
              onChange={(e, value) => handleSeekValue(value)}
              color="primary"
              sx={{
                width: "10rem",
                flex: 1,
                height: 4,
                padding: 0,
                color: "primary.main",
                "& .MuiSlider-thumb": {
                  width: 10,
                  height: 10,
                },
              }}
            />
            <span style={{ fontSize: 12 }}>{formatTime(currentTime)}</span>
          </Box>
          <Button
            sx={{
              textTransform: "none",
              alignSelf: "flex-start",
              width: "fit-content",
              padding: "6px 12px",
            }}
            onClick={handleTranscription}
          >
            {showTranscription ? (
              <>
                <span>Ocultar</span>
                <KeyboardArrowUp style={{ fontSize: 16 }} color="primary" />
              </>
            ) : (
              <>
                <span>Transcrição</span>
                <KeyboardArrowDown style={{ fontSize: 16 }} color="primary" />
              </>
            )}
          </Button>
        </Box>
        {showTranscription && (
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: "#333",
              fontFamily: "Inter, sans-serif",
              fontSize: { xs: 14, sm: 16 },
            }}
          >
            {textTranscribed}
          </Typography>
        )}

        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            textAlign: "right",
            display: "block",
            fontWeight: 500,
            fontSize: "13px",
            lineHeight: "18px",
            letterSpacing: "0px",
            color: "#999EB0",
            fontFamily: "Inter, sans-serif",
          }}
        >
          {msg.time}
        </Typography>
      </Box>
    </Box>
  );
};

export default React.memo(ChatAudioMessage);
